/**
 * B6 (vague « Fluidité ») — Questions des entreprises sur la consultation, côté architecte.
 *
 * L'architecte voit qui a posé chaque question ; il répond UNE fois et la réponse part à
 * toutes les entreprises consultées, sans le nom de l'entreprise qui a demandé (il peut
 * reformuler la question avant la diffusion). L'historique reste dans le dossier de
 * consultation, même une fois l'appel d'offres terminé.
 * Source : GET /api/chantiers/{cid}/dce/{did}/questions,
 *          POST /api/chantiers/{cid}/dce/{did}/questions/{qid}/repondre.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChatCircleText, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return ''; }
};

function FormulaireReponse({ q, onRepondre, envoi }) {
  const [reponse, setReponse] = useState('');
  const [diffusee, setDiffusee] = useState(q.question || '');
  return (
    <div className="mt-2 space-y-2" data-testid={`dce-question-form-${q.numero}`}>
      <label className="block text-xs text-[#6B6B6B]">
        Question telle qu'elle sera diffusée (retirez le nom de l'entreprise si elle le cite)
        <Textarea rows={2} value={diffusee} onChange={(e) => setDiffusee(e.target.value)} className="rounded-none mt-1 text-sm" />
      </label>
      <label className="block text-xs text-[#6B6B6B]">
        Réponse
        <Textarea rows={3} value={reponse} onChange={(e) => setReponse(e.target.value)} className="rounded-none mt-1 text-sm"
          data-testid={`dce-question-reponse-${q.numero}`} />
      </label>
      <div className="flex justify-end">
        <Button size="sm" disabled={envoi || !reponse.trim()} onClick={() => onRepondre(q, reponse, diffusee)}
          className="rounded-none gap-1 bg-[#2D6A4F] hover:bg-[#225538]" data-testid={`dce-question-diffuser-${q.numero}`}>
          <PaperPlaneTilt size={12} /> Répondre à toutes les entreprises
        </Button>
      </div>
    </div>
  );
}

export default function ConsultationQuestionsMoe({ chantierId, dceId }) {
  const [data, setData] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const load = useCallback(async () => {
    if (!dceId) return;
    try {
      const { data: d } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/questions`,
        { withCredentials: true, silent403: true });
      setData(d);
    } catch {
      setData(null);
    }
  }, [chantierId, dceId]);

  useEffect(() => { load(); }, [load]);

  const repondre = async (q, reponse, questionDiffusee) => {
    setEnvoi(true);
    try {
      const { data: r } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/questions/${q.id}/repondre`,
        { reponse, question_diffusee: questionDiffusee }, { withCredentials: true });
      toast.success(r.message);
      setOuverte(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "La réponse n'a pas été diffusée");
    } finally { setEnvoi(false); }
  };

  if (!data) return null;
  const questions = data.questions || [];

  return (
    <div className="border border-[#E0E0DB] bg-white" data-testid="dce-questions">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
        <ChatCircleText size={16} className="text-[#1E3A5F]" />
        <h4 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] flex-1">Questions des entreprises</h4>
        {data.en_attente > 0 && (
          <span className="text-xs font-semibold px-2 py-0.5 bg-[#FFF4E5] text-[#8A5A00]" data-testid="dce-questions-en-attente">
            {data.en_attente} en attente de réponse
          </span>
        )}
      </div>
      {questions.length === 0 ? (
        <p className="px-4 py-3 text-sm text-[#9A9A9A]">
          Aucune question pour l'instant. Les entreprises posent leurs questions depuis leur appel d'offres ;
          votre réponse est communiquée à toutes, sans le nom de l'entreprise qui a demandé.
        </p>
      ) : (
        <ol className="divide-y divide-[#E0E0DB]">
          {questions.map((q) => (
            <li key={q.id} className="px-4 py-3 text-sm" data-testid={`dce-question-${q.numero}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#1E3A5F]">Q{q.numero}</span>
                <span className="text-xs text-[#6B6B6B]">{q.entreprise_nom}{q.lots?.length ? ` · ${q.lots.join(' · ')}` : ''}</span>
                <span className="text-xs text-[#9A9A9A]">· {fmtDate(q.posee_le)}</span>
                <span className={`ml-auto text-[10px] font-bold uppercase ${q.statut === 'repondue' ? 'text-[#2D6A4F]' : 'text-[#D4880F]'}`}>
                  {q.statut_libelle}
                </span>
              </div>
              <p className="mt-1 text-[#2C2C2C] whitespace-pre-wrap">{q.question}</p>
              {q.reponse ? (
                <div className="mt-2 border-l-2 border-[#2D6A4F] pl-3">
                  <p className="text-xs text-[#2D6A4F] font-semibold">
                    Réponse de {q.repondu_par_nom || "l'architecte"}, le {fmtDate(q.repondu_le)} — diffusée à {q.diffusee_a} entreprise{q.diffusee_a > 1 ? 's' : ''}
                  </p>
                  {q.question_diffusee && q.question_diffusee !== q.question && (
                    <p className="text-xs text-[#6B6B6B] italic">Question diffusée : {q.question_diffusee}</p>
                  )}
                  <p className="text-[#2C2C2C] whitespace-pre-wrap">{q.reponse}</p>
                </div>
              ) : data.peut_repondre && (
                ouverte === q.id ? (
                  <FormulaireReponse q={q} onRepondre={repondre} envoi={envoi} />
                ) : (
                  <Button size="sm" variant="outline" className="rounded-none text-xs mt-2" onClick={() => setOuverte(q.id)}
                    data-testid={`dce-question-repondre-${q.numero}`}>
                    Répondre
                  </Button>
                )
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
